import { ApiError } from "@/shared/api";
import { logger } from "@/shared/lib/logger";
import { scrubEvent, scrubText } from "@/shared/lib/scrub";

type Sentry = typeof import("@sentry/browser");
let sentry: Sentry | undefined;

/**
 * Starts Sentry only when a DSN is configured (the SDK is a separate chunk that is never downloaded
 * otherwise). Every event and breadcrumb is scrubbed first: signed URLs and tokens never leave the browser.
 */
export async function initErrorReporting(dsn: string | undefined, environment: string) {
  if (!dsn) return;
  const mod = await import("@sentry/browser");
  mod.init({
    dsn,
    environment,
    beforeSend: (event) => scrubEvent(event),
    beforeBreadcrumb: (crumb) => scrubEvent(crumb),
  });
  sentry = mod;
}

/** Identify the session by user id only (never the email). */
export function setReportingUser(id: string | undefined) {
  sentry?.setUser(id ? { id } : null);
}

export interface ReportContext {
  /** Where it happened: "route:/drive", "preview", "upload-panel"... */
  where: string;
}

/** Log (scrubbed) and, when configured, send to Sentry with the request id and location. */
export function reportError(error: unknown, context: ReportContext) {
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  const message = error instanceof Error ? error.message : String(error);
  logger.error(
    `Unhandled error in ${context.where}: ${scrubText(message)}`,
    requestId ? { requestId } : undefined,
  );
  if (!sentry) return;
  sentry.withScope((scope) => {
    scope.setTag("where", context.where);
    if (requestId) scope.setTag("request_id", requestId);
    sentry?.captureException(error);
  });
}
